import React, { Dispatch, SetStateAction } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import TimePicker from 'react-time-picker';
import { UnistylesRuntime } from 'react-native-unistyles';
import DatePicker from 'react-date-picker';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../../MyAppText';
import '../../../styles/date-picker.css';
import { Controller, UseFormReturn } from 'react-hook-form';
import inputStylesheet from '../../../styles/inputStyles';

type FilterDatePickerProps = {
    label: string;
    editable?: boolean;
    maxDate?: Date;
    zIndex?: number;
    onCalendarOpen?: () => void;
    onCalendarClose?: () => void;
    required?: boolean;
    disabled?: boolean;
    labelStyle?: StyleProp<TextStyle>;
} & ({
    form?: undefined;
    dateFieldName?: undefined;
    timeFieldName?: undefined;
    date: CalendarValue;
    onChangeDate: Dispatch<SetStateAction<CalendarValue>>;
    time: string;
    onChangeTime: (value: string) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form?: UseFormReturn<any, any, any>;
    dateFieldName: string;
    timeFieldName: string;
    date?: undefined;
    onChangeDate?: undefined;
    time?: undefined;
    onChangeTime?: undefined;
});

type ValuePiece = Date | null;
export type CalendarValue = ValuePiece;

export default function FilterDatePicker({ label, date, time, onChangeDate, onChangeTime, maxDate, zIndex, onCalendarOpen, onCalendarClose, required, disabled, labelStyle, form, dateFieldName, timeFieldName }: FilterDatePickerProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    if (form && dateFieldName && timeFieldName) {
        return (
            <View style={[defaultInputStyle.container, zIndex ? { zIndex: zIndex } : {}]}>
                <View style={defaultInputStyle.labelContainer}>
                    <MyAppText style={labelStyle ?? defaultInputStyle.label}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row', columnGap: 5, flex: 1 }}>
                    <View style={[styles.container({}), { flexGrow: 2 }]}>
                        <Controller
                            control={form.control}
                            name={dateFieldName}
                            render={({ field }) => (
                                <DatePicker
                                    calendarProps={{ locale: 'pt-BR' }}
                                    maxDate={maxDate}
                                    onChange={(val) => {
                                        field.onChange(val);
                                    }}
                                    autoFocus={false}
                                    shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                                    value={field.value}
                                    clearIcon={null}
                                    disabled={disabled}
                                    locale='pt-BR'
                                    className={[
                                        'light', 'lightAlt'
                                    ].includes(UnistylesRuntime.themeName)
                                        ? ['inputColor', `inputColorLight`]
                                        : ['inputColor', `inputColorDark`]
                                    }
                                    calendarIcon={<FontAwesomeIcon icon={faCalendar} color={theme.colors.iconColor}></FontAwesomeIcon>}
                                    onCalendarClose={onCalendarClose}
                                    onCalendarOpen={onCalendarOpen}
                                />
                            )}
                        />

                    </View>
                    <View style={styles.container({})}>
                        <Controller
                            control={form.control}
                            name={timeFieldName}
                            render={({ field }) => (
                                <TimePicker
                                    disableClock={true}
                                    format='HH:mm'
                                    locale='pt-BR'
                                    maxDetail='minute'
                                    disabled={disabled}
                                    className={[
                                        'light', 'lightAlt'
                                    ].includes(UnistylesRuntime.themeName)
                                        ? ['inputColor', `inputColorLight`]
                                        : ['inputColor', `inputColorDark`]
                                    }
                                    value={field.value}
                                    clearIcon={null}
                                    onChange={(value) => {
                                        if (value) {
                                            field.onChange(value);
                                        }
                                    }}
                                />
                            )}
                        />
                    </View>
                </View>
            </View>
        );
    } else if (date !== undefined && onChangeDate !== undefined && time !== undefined && onChangeTime !== undefined) {
        return (
            <View style={[defaultInputStyle.container, zIndex ? { zIndex: zIndex } : {}]}>
                <View style={defaultInputStyle.labelContainer}>
                    <MyAppText style={labelStyle ?? defaultInputStyle.label}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row', columnGap: 5, flex: 1 }}>
                    <View style={[styles.container({}), { flexGrow: 2 }]}>
                        <DatePicker
                            calendarProps={{ locale: 'pt-BR' }}
                            maxDate={maxDate}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            onChange={onChangeDate}
                            autoFocus={false}
                            shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                            value={date}
                            clearIcon={null}
                            disabled={disabled}
                            locale='pt-BR'
                            className={[
                                'light', 'lightAlt'
                            ].includes(UnistylesRuntime.themeName)
                                ? ['inputColor', `inputColorLight`]
                                : ['inputColor', `inputColorDark`]
                            }
                            calendarIcon={<FontAwesomeIcon icon={faCalendar} color={theme.colors.iconColor}></FontAwesomeIcon>}
                            onCalendarClose={onCalendarClose}
                            onCalendarOpen={onCalendarOpen}
                        />
                    </View>
                    <View style={styles.container({})}>
                        <TimePicker
                            disableClock={true}
                            format='HH:mm'
                            locale='pt-BR'
                            maxDetail='minute'
                            disabled={disabled}
                            className={[
                                'light', 'lightAlt'
                            ].includes(UnistylesRuntime.themeName)
                                ? ['inputColor', `inputColorLight`]
                                : ['inputColor', `inputColorDark`]
                            }
                            value={time}
                            clearIcon={null}
                            onChange={(value) => {
                                if (value) {
                                    onChangeTime(value);
                                }
                            }}
                        />
                    </View>
                </View>
            </View>
        );
    }
}


const styleSheet = createStyleSheet((theme) => ({
    filterLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
    container: ({ zIndex }: { zIndex?: number; }) => {
        const { styles: defaultInputStyle } = useStyles(inputStylesheet);
        const placeholder = defaultInputStyle.input({ placeholder: true });
        const defaultInput = defaultInputStyle.input({});
        const invalid = defaultInputStyle.input({ invalid: true });

        return {
            zIndex,
            '--daterange-placeholder-color': placeholder.color,
            '--daterange-invalid-border-color': invalid.borderColor,
            '--daterange-text-color': defaultInput.color,
            '--daterange-border-color': defaultInput.borderColor,
            '--daterange-button-color': defaultInput.borderColor,
            '--daterange-border-radius': '6px',
            '--daterange-font-size': '14px',
            '--daterange-font-weight': '500',

            '--daterange-calendar-background-color': defaultInput.backgroundColor,
            '--daterange-calendar-border-color': defaultInput.borderColor,
            '--daterange-calendar-days-text-color': theme.colors.calendar.dayText,
            '--daterange-calendar-navigation-text-color': theme.colors.calendar.navigationText,
            '--daterange-calendar-navigation-text-disabled-color': theme.colors.calendar.navigationDisabledText,
            '--daterange-calendar-navigation-background-hover-color': theme.colors.calendar.navigationBackgroundHover,
            '--daterange-calendar-days-background-selected-active-color': theme.colors.calendar.dayBackgroundSelectedActive,
            '--daterange-calendar-days-background-selected-range-color': theme.colors.calendar.dayBackgroundSelectedRange,
            '--daterange-calendar-days-text-selected-range-color': theme.colors.calendar.dayTextSelectedRangeColor,
            '--daterange-calendar-days-text-disabled-color': theme.colors.calendar.dayTextDisabledColor,
        };
    },
}));
